import { useQuery } from "@apollo/client";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import Delete from "@mui/icons-material/Delete";
import Download from "@mui/icons-material/Download";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import HourglassEmpty from "@mui/icons-material/HourglassEmpty";
import LinkOff from "@mui/icons-material/LinkOff";
import ListAlt from "@mui/icons-material/ListAlt";
import MoreVert from "@mui/icons-material/MoreVert";
import OpenInNew from "@mui/icons-material/OpenInNew";
import Visibility from "@mui/icons-material/Visibility";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Popover,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { SparkleIcon } from "components/ArtificialIntelligence/SparkleIcon";
import { ConfirmDialog } from "components/ConfirmDialog";
import { DocumentSwitcherSelect } from "components/Document/ReviewDocumentMetadataDialog/components/DocumentSwitcherSelect";
import {
  MANIFEST_ATTENTION_ERROR_KINDS,
  MANIFEST_ATTENTION_SECTIONS,
  listManifestAttention,
} from "components/Document/ReviewDocumentMetadataDialog/components/manifestAttention";
import { ManifestDetailSection } from "components/Document/ReviewDocumentMetadataDialog/components/ManifestDetailSection";
import { ManifestFieldOffers } from "components/Document/ReviewDocumentMetadataDialog/components/ManifestFieldOffers";
import { ManifestFillProgress } from "components/Document/ReviewDocumentMetadataDialog/components/ManifestFillProgress";
import { ManifestKeyboardHints } from "components/Document/ReviewDocumentMetadataDialog/components/ManifestKeyboardHints";
import {
  indexReadingOffers,
  indexSourceOffers,
  manifestFormValue,
  manifestOffersForPath,
  mergeOfferIndexes,
  pathsWithOffers,
  rowsWithOffers,
} from "components/Document/ReviewDocumentMetadataDialog/components/manifestSourceOffers";
import { indexReviewFieldDiffs } from "components/Document/ReviewDocumentMetadataDialog/components/reviewFieldDiffs";
import { useDocumentFollowing } from "components/Document/ReviewDocumentMetadataDialog/hooks/useDocumentFollowing";
import { useDocumentMetadata } from "components/Document/ReviewDocumentMetadataDialog/hooks/useDocumentMetadata";
import { useManifestReviewSection } from "components/Document/ReviewDocumentMetadataDialog/hooks/useManifestReviewSection";
import {
  AttentionListButton,
  AttentionTargetsProvider,
  fieldPathOf,
  type OfferedValue,
  PublishTravelTargets,
  REVEAL_SHORTCUTS_PER_FIELD,
  travellablePaths,
  useFocusedFieldOffers,
  useKeepOnTabAway,
  useAttentionTargetSweep,
  useAttentionTravel,
  useReviewAcknowledgements,
} from "components/Document/ReviewInteraction";
import { DocumentDialogContainer, FullScreenDocumentPreview } from "components/Document/shared";
import { FieldAccessoryProvider } from "components/Forms/FieldAccessory";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { getUtcDisplayDate } from "encamp-shared/src/utils/date";
import { gql } from "generated-graphql";
import { ExtractionStatus } from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useDocumentDownload } from "hooks/useDocumentDownload";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useWarnBeforeUnload } from "hooks/useWarnBeforeUnload";
import { get } from "lodash-es";
import { ReactNode, useCallback, useMemo, useRef, useState } from "react";
import { hasCriticalIssues } from "util/forms";

import { ManifestCommentsPopover } from "./ManifestCommentsPopover";
import { canOfferDestructiveReviewActions } from "./manifestReviewActions";
import { manifestReviewHeader } from "./manifestReviewHeader";

const PROCESSED_MANIFEST_FOR_REVIEW_DOCUMENT = gql(`
  query ProcessedManifestForReviewDocument($id: ID!) {
    document(id: $id) {
      id
      sourceProcessedManifest {
        id
      }
    }
  }
`);

const MANIFEST_REVIEW_FIELD_DIFFS = gql(`
  query ManifestReviewFieldDiffs($processedManifestId: ID!) {
    manifestReviewFieldDiffs(processedManifestId: $processedManifestId) {
      availableSources
      fieldDiffs {
        field
        wasteLineNumber
        discrepant
        eManifest
        vendor
        document
      }
      sourceValues {
        field
        wasteLineNumber
        source
        value
        formValue
      }
    }
  }
`);

export type ManifestReviewDialogProps = {
  onClose: () => void;
  /** Document IDs to review. When more than one, the dialog navigates between them. */
  documentIds: string[];
  /**
   * Pre-resolved waste manifest ID. When omitted, the dialog falls back to
   * the document's sourceWasteManifest (set after extraction completes).
   */
  wasteManifestId?: string;
  /** Called after a successful save+next on the last document. */
  onComplete?: () => void;
  /** Refresh the host after persisted manifest or document changes. */
  onManifestChanged?: () => void;
  /**
   * Which document is open, when the host owns that.
   *
   * A route hosting this review keeps the position in the URL, so a refresh, a
   * back button or a shared link all land on the document the reviewer was
   * actually on rather than back at the start of the batch. Omit it and the
   * dialog keeps its own position, which is what every non-routed caller wants.
   */
  currentDocumentId?: string;
  /** Set alongside `currentDocumentId` — where the dialog asks to be moved to. */
  onNavigate?: (documentId: string) => void;
};

/**
 * Manifest-only review dialog. Single-form layout (no tabs):
 *  - Title bar: Info-icon Popover with doc metadata + an "Open in Documents"
 *    deep link; More-Actions menu (Detach/Delete).
 *  - Left pane: manifest fields form
 *  - Right pane: PDF preview
 *
 * Distinct from the shared ReviewDocumentMetadataDialog (which is general
 * document AI). Manifest entry points (ManifestUploadDialog, the Documents
 * section's source-document action) open this dialog. Reuses
 * ManifestDetailSection + useManifestReviewSection.
 */
/**
 * Travel needs the registry mounted above every hook that reaches for it — the
 * target sweep, the position the reviewer's own clicks set, and the jump control
 * all read it — so the provider wraps the dialog rather than sitting inside its
 * tree. The extraction grid does the same thing by having the provider above its
 * field components; this form is one component, so the split is explicit.
 */
export function ManifestReviewDialog(props: ManifestReviewDialogProps) {
  const { acknowledged, acknowledge } = useReviewAcknowledgements();
  return (
    <AttentionTargetsProvider acknowledged={acknowledged} acknowledge={acknowledge}>
      <ManifestReviewDialogContent
        {...props}
        acknowledged={acknowledged}
        acknowledge={acknowledge}
      />
    </AttentionTargetsProvider>
  );
}

/**
 * Where "jump to next" can take the reviewer.
 *
 * Wider than the attention list, and deliberately so. The list is what is
 * *flagged* — validation errors, comparator discrepancies, readings the model
 * doubted — and its count is read as a problem count. Travel answers a different
 * question: what should I look at before confirming. A field another source
 * holds a value for belongs in that answer whether or not the comparators called
 * it a discrepancy, and often they cannot: they examine the generator's site
 * address and never its mailing address, so a mailing field can show a chip
 * carrying e-Manifest's value that nothing will ever flag.
 *
 * Kept out of `attention` so widening travel does not inflate that badge.
 */
function useTravelPaths({
  attention,
  sourceOffers,
  rowNumbers,
  offersFor,
}: {
  attention: Parameters<typeof travellablePaths>[0];
  sourceOffers: Parameters<typeof pathsWithOffers>[0];
  rowNumbers: Parameters<typeof pathsWithOffers>[1];
  /** What a field has on offer, read from the form rather than subscribed to. */
  offersFor: (path: string) => { offers: readonly unknown[]; canKeep: boolean };
}): { travelPaths: string[]; isWorthStopping: (path: string) => boolean } {
  const offeredPaths = useMemo(
    () => pathsWithOffers(sourceOffers, rowNumbers),
    [sourceOffers, rowNumbers]
  );
  const flagged = useMemo(() => new Set(travellablePaths(attention)), [attention]);

  const travelPaths = useMemo(
    () => [...new Set([...flagged, ...offeredPaths])],
    [flagged, offeredPaths]
  );

  // A flagged field always has something to show — the flag is the reason. An
  // offer-only field has to be asked, because its chips are filtered against the
  // value in the field: a source that agrees with what is there renders nothing,
  // and stopping at a field showing no chips reads as travel going nowhere.
  const isWorthStopping = useCallback(
    (path: string) => {
      if (flagged.has(path)) return true;
      const { offers, canKeep } = offersFor(path);
      return offers.length > 0 || canKeep;
    },
    [flagged, offersFor]
  );

  return { travelPaths, isWorthStopping };
}

function ManifestReviewDialogContent({
  onClose,
  documentIds,
  wasteManifestId: initialWasteManifestId,
  onComplete,
  onManifestChanged,
  currentDocumentId: controlledDocumentId,
  onNavigate,
  acknowledged,
  acknowledge,
}: ManifestReviewDialogProps & {
  acknowledged: ReadonlySet<string>;
  acknowledge: (path: string) => void;
}) {
  const alerts = useAlerts();
  const { featureFlags } = useFeatureFlags();
  const { isStaff } = useCurrentUser();
  const hasDocumentAI = !!featureFlags[FeatureFlagKey.DocumentAI];
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Position lives here only when the host is not keeping it. Both branches are
  // always present so the hook order never changes with the prop.
  const [ownDocumentId, setOwnDocumentId] = useState<string | undefined>(() => documentIds[0]);
  const currentDocumentId = controlledDocumentId ?? ownDocumentId;
  const [moreActionsAnchor, setMoreActionsAnchor] = useState<null | HTMLElement>(null);
  const [docInfoAnchor, setDocInfoAnchor] = useState<null | HTMLElement>(null);
  const [showDetachConfirm, setShowDetachConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [showSkipConfirm, setShowSkipConfirm] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [fullScreenOpen, setFullScreenOpen] = useState(false);

  const { fetchAndHandleDownload } = useDocumentDownload();

  const currentIndex = useMemo(() => {
    if (!currentDocumentId) return 0;
    const i = documentIds.indexOf(currentDocumentId);
    return i >= 0 ? i : 0;
  }, [currentDocumentId, documentIds]);
  const hasNext = currentIndex < documentIds.length - 1;

  const { document, loading, previewUrl } = useDocumentMetadata({
    documentId: currentDocumentId,
    open: true,
    hasDocumentAI,
  });

  const resolvedWasteManifestId =
    initialWasteManifestId ?? document?.sourceWasteManifest?.id ?? undefined;

  // Comments anchor on the ProcessedManifest, which is created asynchronously by
  // the process-manifests job. During the brief post-ingest window it may not
  // exist yet — the comment affordance stays hidden until it does.
  const { data: pmForDocData } = useQuery(PROCESSED_MANIFEST_FOR_REVIEW_DOCUMENT, {
    variables: { id: currentDocumentId ?? "" },
    skip: !currentDocumentId,
    fetchPolicy: "cache-and-network",
  });
  const commentsProcessedManifestId = pmForDocData?.document?.sourceProcessedManifest?.id;

  // Review-time per-field diffs: which parsed document values disagree with what
  // we already know (e-Manifest / vendor). Computed live server-side because the
  // document-axis discrepancies aren't persisted until the reviewer confirms.
  const { data: fieldDiffsData } = useQuery(MANIFEST_REVIEW_FIELD_DIFFS, {
    variables: { processedManifestId: commentsProcessedManifestId ?? "" },
    skip: !commentsProcessedManifestId,
    fetchPolicy: "cache-and-network",
  });
  const reviewFieldDiffIndex = useMemo(
    () => indexReviewFieldDiffs(fieldDiffsData?.manifestReviewFieldDiffs?.fieldDiffs ?? []),
    [fieldDiffsData]
  );
  const reviewAvailableSources = useMemo(
    () => fieldDiffsData?.manifestReviewFieldDiffs?.availableSources ?? [],
    [fieldDiffsData]
  );

  const manifest = useManifestReviewSection({
    wasteManifestId: resolvedWasteManifestId,
    documentId: currentDocumentId,
    onManifestChanged,
  });

  // The form's rows in their rendered order, so a focused path's index resolves
  // to the printed line number the reading captured a page against.
  const rowNumbers = useMemo(
    () => ({
      wasteLines: manifest.wasteLineFieldArray.fields.map((line) => line.wasteLineNumber),
      transporters: manifest.transporterFieldArray.fields.map(
        (transporter) => transporter.transporterLineNumber
      ),
    }),
    [manifest.wasteLineFieldArray.fields, manifest.transporterFieldArray.fields]
  );

  // Tied to the document it was measured on, so the count from the previous
  // manifest in the queue cannot decide where this one's boxes are.
  const [previewPages, setPreviewPages] = useState<{ documentId: string; count: number } | null>(
    null
  );
  const notePreviewPageCount = useCallback(
    (count: number) => {
      if (currentDocumentId == null) return;
      setPreviewPages({ documentId: currentDocumentId, count });
    },
    [currentDocumentId]
  );

  const following = useDocumentFollowing({
    pages: manifest.readingPages,
    rowNumbers,
    // Manifests read before readings were kept apart from the working copy carry
    // no page numbers at all, which left the pane doing nothing on them. How long
    // the file is turns that into a placeable guess rather than a refusal.
    pageCount:
      previewPages != null && previewPages.documentId === currentDocumentId
        ? previewPages.count
        : null,
  });

  const { markPosition } = useAttentionTravel();

  // The in-app close asks before discarding; the browser's own exits did not. On
  // a transcription screen a stray refresh is the expensive one.
  useWarnBeforeUnload(manifest.manifestIsDirty);

  const attention = useMemo(
    () =>
      listManifestAttention({
        issues: manifest.manifestIssues,
        fieldDiffs: reviewFieldDiffIndex,
        confidences: manifest.fieldConfidences,
        rows: rowNumbers,
        acknowledged,
      }),
    [
      manifest.manifestIssues,
      manifest.fieldConfidences,
      reviewFieldDiffIndex,
      rowNumbers,
      acknowledged,
    ]
  );

  // Re-swept when a repeated group gains or loses a row, since that changes which
  // field names exist and a stale registry travels to a control that has gone.
  const targetsRef = useAttentionTargetSweep([
    manifest.manifestDataLoaded,
    rowNumbers.wasteLines.length,
    rowNumbers.transporters.length,
  ]);

  // Paths anything is currently asking about, so a "keep this" answer is offered
  // exactly where there is a flag to clear.
  const attentionPaths = useMemo(
    () => new Set(attention.filter((item) => item.kind !== "issue").map((item) => item.path)),
    [attention]
  );

  // What the document itself said, ahead of what the other pillars hold: it is
  // the only offer that answers "what did the AI actually read", which is the
  // question a reviewer who has typed over a value has when they want it back.
  const sourceOffers = useMemo(
    () =>
      mergeOfferIndexes(
        indexReadingOffers(manifest.readingOffers),
        indexSourceOffers(fieldDiffsData?.manifestReviewFieldDiffs?.sourceValues ?? [])
      ),
    [manifest.readingOffers, fieldDiffsData]
  );

  // Which repeated rows have something to say, so they open themselves rather
  // than hiding it behind a summary no keyboard can get past. Read from the raw
  // index, which only moves when a query lands — a default that shrank as the
  // reviewer answered offers would fold a row up around the caret in it.
  const openRows = useMemo(() => rowsWithOffers(sourceOffers), [sourceOffers]);

  // What every other source holds for a field, resolved per path.
  //
  // Filtered against what the field holds now, the way the extraction grid
  // filters its own: a source agreeing with the form is not an offer, and a row
  // that reads "e-Manifest 141" beside "Keep 141" asks the reviewer to compare
  // two identical values. Filtering on the current value rather than a load-time
  // flag is also what keeps taking one reversible — the value it replaced is
  // immediately on offer again.
  // Cached per path, and the cache thrown away whenever its inputs change.
  //
  // Answering with a fresh array each call is what a plain lookup does, and it
  // is enough to stop every offer row on the form from being skippable: sixty
  // rows are asked this during render, and a new array is a new prop. The cache
  // makes the answer to "what does this field have on offer" the same object
  // until something actually changes it.
  const candidatesFor = useMemo(() => {
    const cache = new Map<string, { candidates: OfferedValue<string>[]; canKeep: boolean }>();
    return (path: string) => {
      const cached = cache.get(path);
      if (cached != null) return cached;
      const answer = {
        candidates: manifestOffersForPath(sourceOffers, path, rowNumbers),
        // Something is asking about this field, so keeping what it holds is an
        // answer worth being able to give.
        canKeep: attentionPaths.has(path),
      };
      cache.set(path, answer);
      return answer;
    };
  }, [sourceOffers, rowNumbers, attentionPaths]);

  // What the keyboard acts on, resolved when the key is pressed rather than
  // during render. The chips each exclude their own field's value, and the
  // shortcuts are numbered by position in that same row, so the two have to
  // agree — reading the value here at event time keeps them in step without the
  // dialog subscribing to the form to find out.
  const offersFor = useCallback(
    (path: string) => {
      const { candidates, canKeep } = candidatesFor(path);
      // The whole snapshot then a path read, because the accessory addresses
      // fields by plain string and `getValues` narrows only a statically known
      // path. It is a read, not a subscription, so it costs a keystroke rather
      // than a render.
      const current = get(manifest.manifestGetValues(), path);
      const currentText = current == null ? "" : String(current);
      return {
        offers: candidates.filter((offer) => String(offer.value) !== currentText),
        canKeep,
      };
    },
    [candidatesFor, manifest]
  );

  // The reviewer chose the value, so it is theirs: an ordinary dirty edit,
  // recorded as reviewer-entered rather than document-extracted.
  //
  // Read into the terms the field holds first. Every offer carries a string, and
  // two of these fields are typed `number | null` — writing a string into them
  // sends one to a `Float`/`Int` input and the save fails variable coercion, with
  // the reviewer unable to commit until they retype the value.
  // Held as its own binding so the callback is stable across renders: it is
  // handed to every offer row on the form, and one that changed each render
  // would re-render all of them.
  const { manifestSetValue } = manifest;
  const takeAt = useCallback(
    (path: string, offer: OfferedValue<string>) =>
      manifestSetValue(
        path as Parameters<typeof manifestSetValue>[0],
        manifestFormValue(path, offer.value),
        { shouldDirty: true }
      ),
    [manifestSetValue]
  );

  const { travelPaths, isWorthStopping } = useTravelPaths({
    attention,
    sourceOffers,
    rowNumbers,
    offersFor,
  });

  // Only the keyboard half now: which field the caret is in, and which keystroke
  // takes which of its offers. The chips themselves render inside each field.
  // Tabbing off a flagged field answers it. This form acknowledges by path, so
  // the path is its own answer key.
  const keepOnTab = useKeepOnTabAway({
    answerKeyForPath: useCallback(
      (path: string) => (attentionPaths.has(path) ? path : undefined),
      [attentionPaths]
    ),
    acknowledge,
  });

  const offers = useFocusedFieldOffers({
    offersFor,
    onTake: takeAt,
    onKeep: acknowledge,
  });

  /**
   * Every field's offers, inline under its own input — the treatment the
   * extraction grid already gives them, so a reviewer moving between the two
   * surfaces meets one affordance.
   *
   * Not gated on focus. A row that appeared and vanished with the caret would
   * relayout the form on every tab, and a reviewer cannot act on help they have
   * to visit each of sixty fields to discover. The keystroke badges are the part
   * that belongs to the focused field, and CSS reveals those.
   */
  const renderFieldOffers = useCallback(
    ({ name, label }: { name: string; label?: ReactNode }) => {
      const { candidates, canKeep } = candidatesFor(name);
      if (candidates.length === 0 && !canKeep) return null;
      // Each row reads its own field's value, so typing re-renders the row being
      // typed in and nothing else. Deciding here what the row holds would put
      // the whole form's values in this callback, and this callback is the
      // provider's `render` prop — a new one on every keystroke re-renders every
      // field under it.
      return (
        <ManifestFieldOffers
          control={manifest.manifestControl}
          name={name}
          label={label}
          candidates={candidates}
          canKeep={canKeep}
          onTake={takeAt}
          onKeep={acknowledge}
        />
      );
    },
    [candidatesFor, takeAt, acknowledge, manifest.manifestControl]
  );

  // Download the source document (PDF/image) so reviewers can open it on a
  // larger screen instead of working through the inline preview. Reuses the
  // shared download hook, which fetches a fresh signed link and a real filename.
  const handleDownloadDocument = useCallback(async () => {
    if (!currentDocumentId) return;
    setIsDownloading(true);
    try {
      await fetchAndHandleDownload({ documentId: currentDocumentId });
    } finally {
      setIsDownloading(false);
    }
  }, [currentDocumentId, fetchAndHandleDownload]);

  const navigateToDocument = useCallback(
    (docId: string) => {
      if (onNavigate != null) onNavigate(docId);
      else setOwnDocumentId(docId);
      scrollContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    },
    [onNavigate]
  );

  // Going backwards in a multi-doc review: navigate without saving. The user
  // can hit Save & Next on the way forward again if they want to commit.
  const handlePrevious = useCallback(() => {
    if (currentIndex > 0) {
      navigateToDocument(documentIds[currentIndex - 1]);
    }
  }, [currentIndex, documentIds, navigateToDocument]);

  // Skip the current doc forward without saving. Confirms first so users
  // understand the doc stays in needs-review state until they come back to it.
  const handleSkipConfirm = useCallback(() => {
    setShowSkipConfirm(false);
    if (currentIndex < documentIds.length - 1) {
      navigateToDocument(documentIds[currentIndex + 1]);
    }
  }, [currentIndex, documentIds, navigateToDocument]);

  // Jump to any doc in the batch via the title-bar switcher.
  const handleSwitcherSelect = useCallback(
    (docId: string) => {
      if (docId !== currentDocumentId) {
        navigateToDocument(docId);
      }
    },
    [currentDocumentId, navigateToDocument]
  );

  // Wrap close attempts: if the form is dirty, prompt to discard. Otherwise
  // close immediately. Used by the X button, Cancel button, and backdrop clicks.
  // When closing an unreviewed doc with no pending edits, reassure the reviewer
  // that walking away costs nothing — the document stays attached and waiting.
  // This branch is the clean-close one, so it must not claim a write: "saved"
  // described a mutation that never ran.
  const attemptClose = useCallback(() => {
    if (manifest.manifestIsDirty) {
      setShowDiscardConfirm(true);
    } else {
      if (manifest.manifestNeedsReview) {
        alerts.info("No changes to save. The document stays attached and marked Needs Review.");
      }
      onClose();
    }
  }, [manifest.manifestIsDirty, manifest.manifestNeedsReview, onClose, alerts]);

  // After detach/delete: if there are more docs in the queue, advance to the
  // next one so the user can keep reviewing. Otherwise close. Without this,
  // a destructive action on doc N collapses the rest of the queue.
  const advanceOrClose = useCallback(() => {
    if (hasNext) {
      navigateToDocument(documentIds[currentIndex + 1]);
    } else {
      onClose();
    }
  }, [hasNext, currentIndex, documentIds, navigateToDocument, onClose]);

  // Primary action: when the doc still needs review this marks it reviewed
  // (clearing needsReview); once reviewed it's a plain edit-save. Either way it
  // advances to the next doc / closes on the last.
  const handleSaveAndNext = useCallback(async () => {
    const markReviewed = manifest.manifestNeedsReview;
    try {
      await manifest.saveManifest({ markReviewed });
      if (markReviewed || manifest.manifestIsDirty) {
        alerts.success(markReviewed ? "Manifest marked as reviewed." : "Manifest saved.");
      }
      if (hasNext) {
        navigateToDocument(documentIds[currentIndex + 1]);
      } else {
        onComplete?.();
        onClose();
      }
    } catch (err) {
      alerts.error("Failed to save manifest.", err instanceof Error ? err : undefined);
    }
  }, [
    manifest,
    hasNext,
    currentIndex,
    documentIds,
    navigateToDocument,
    onComplete,
    onClose,
    alerts,
  ]);

  // Partial save: persist edits without marking the doc reviewed, and stay on
  // it. Lets a reviewer pause mid-document (e.g. to ask about a handwritten line
  // item) without losing work or prematurely clearing the Needs Review flag.
  const handlePartialSave = useCallback(async () => {
    try {
      await manifest.saveManifest({ markReviewed: false });
      alerts.success("Progress saved. This manifest stays marked Needs Review.");
    } catch (err) {
      alerts.error("Failed to save manifest.", err instanceof Error ? err : undefined);
    }
  }, [manifest, alerts]);

  const isSaving = manifest.manifestSaving;
  const isLoading = loading || manifest.manifestLoading;
  const isBusy = isSaving || isLoading;

  // Block save/confirm on critical issues — currently a missing Manifest
  // Tracking Number (NOT NULL on WasteManifest). Other manifest validation is
  // warn-and-allow (ERROR level) and does not block. The offending field shows
  // its error inline.
  const hasBlockingIssues = hasCriticalIssues(manifest.manifestIssues ?? []);

  const needsReview = manifest.manifestNeedsReview;
  // Still-flagged data reads as a review task; once reviewed it's an edit of
  // the saved document data.
  const { title: dialogTitle, subheader: dialogSubheader } = manifestReviewHeader({
    trackingNumber: manifest.manifestWatch("manifestTrackingNumber"),
    // The org name answers "whose manifest is this", which is only a question
    // on the cross-tenant staff queue. A customer is inside their own org and
    // reads it as noise.
    tenantName: isStaff ? manifest.facility?.tenant?.name : null,
    facilityName: manifest.facility?.name,
    index: currentIndex,
    total: documentIds.length,
  });
  let primaryActionLabel: string;
  if (isSaving) {
    primaryActionLabel = "Saving…";
  } else {
    // When still flagged, the primary action marks the doc reviewed — name it so
    // (not "Confirm"); once reviewed it's a plain Save. During initial load the
    // button is disabled (via isBusy) but keeps its normal label.
    const verb = needsReview ? "Mark Reviewed" : "Save";
    primaryActionLabel = hasNext ? `${verb} & Next` : verb;
  }

  const canShowActions = !!currentDocumentId && manifest.manifestDataLoaded;
  const canShowDestructiveActions = canOfferDestructiveReviewActions({
    documentLoaded: canShowActions,
    isStaff,
  });
  const tags = document?.documentTags ?? [];
  const authoredAtDisplay = document?.authoredAt
    ? getUtcDisplayDate(document.authoredAt, "med")
    : "—";

  // Popover content: doc metadata + a deep link out to the document library.
  // Triggered by the "i" Info icon next to the More-Actions menu in the title bar.
  const documentLibraryHref =
    document?.tenantId && document?.id ? `/o/${document.tenantId}/documents/${document.id}` : null;
  const docInfoPopover = (
    <Stack spacing={1.25} sx={{ p: 2, maxWidth: 360 }}>
      <InfoRow label="Title" value={document?.title ?? "—"} />
      <InfoRow label="Document type" value={document?.documentType ?? "—"} />
      <InfoRow
        label="File"
        value={document?.fileExtension ? document.fileExtension.toUpperCase() : "—"}
      />
      <InfoRow label="Date authored" value={authoredAtDisplay} />
      <InfoRow label="Description" value={document?.description ?? "—"} />
      <InfoRow
        label="Tags"
        value={
          tags.length === 0 ? (
            "—"
          ) : (
            <Stack direction="row" flexWrap="wrap" gap={0.5}>
              {tags.map((tag) => (
                <Chip key={tag.id} label={tag.name} size="small" variant="outlined" />
              ))}
            </Stack>
          )
        }
      />
      {documentLibraryHref && (
        <Link
          href={documentLibraryHref}
          target="_blank"
          rel="noopener noreferrer"
          variant="body2"
          onClick={() => setDocInfoAnchor(null)}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            pt: 0.5,
          }}
        >
          Manage document
          <OpenInNew fontSize="inherit" />
        </Link>
      )}
    </Stack>
  );

  return (
    <>
      <PublishTravelTargets paths={travelPaths} isWorthStopping={isWorthStopping} />
      <DocumentDialogContainer
        open
        onClose={attemptClose}
        fullWidth
        maxWidth="xl"
        title={dialogTitle}
        subheader={dialogSubheader || undefined}
        titleBarActionElement={
          <>
            {canShowActions && manifest.manifestNeedsReview && (
              <Chip
                icon={<SparkleIcon sx={{ fontSize: 16 }} />}
                label="Needs review"
                size="small"
                color="warning"
                variant="outlined"
                sx={{ mr: 0.5 }}
              />
            )}
            {isStaff && commentsProcessedManifestId && (
              <ManifestCommentsPopover processedManifestId={commentsProcessedManifestId} />
            )}
            {previewUrl && (
              <Tooltip title="Full-screen preview">
                <IconButton
                  size="small"
                  onClick={() => setFullScreenOpen(true)}
                  aria-label="Open full-screen preview"
                  sx={{ mr: 0.5 }}
                >
                  <Visibility sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
            {previewUrl && currentDocumentId && (
              <Tooltip title="Download document">
                {/* span wrapper lets the tooltip work while the button is
                    disabled mid-download */}
                <span>
                  <IconButton
                    size="small"
                    onClick={handleDownloadDocument}
                    disabled={isDownloading}
                    aria-label="Download document"
                    sx={{ mr: 0.5 }}
                  >
                    {isDownloading ? (
                      <CircularProgress size={18} />
                    ) : (
                      <Download sx={{ fontSize: 18 }} />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {canShowActions && (
              <>
                {documentIds.length > 1 && (
                  <DocumentSwitcherSelect
                    documentIds={documentIds}
                    tenantId={document?.tenantId ?? undefined}
                    selectedId={currentDocumentId}
                    onSelect={handleSwitcherSelect}
                    minWidth={260}
                    maxWidth={300}
                  />
                )}
                <Tooltip title="Document info">
                  <IconButton
                    size="small"
                    onClick={(e) => setDocInfoAnchor(e.currentTarget)}
                    aria-label="Document info"
                  >
                    <ListAlt />
                  </IconButton>
                </Tooltip>
                <Popover
                  open={Boolean(docInfoAnchor)}
                  anchorEl={docInfoAnchor}
                  onClose={() => setDocInfoAnchor(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                >
                  {docInfoPopover}
                </Popover>
                {canShowDestructiveActions && (
                  <>
                    <Tooltip title="More actions">
                      <IconButton
                        size="small"
                        onClick={(e) => setMoreActionsAnchor(e.currentTarget)}
                        aria-label="More actions"
                      >
                        <MoreVert />
                      </IconButton>
                    </Tooltip>
                    <Menu
                      anchorEl={moreActionsAnchor}
                      open={Boolean(moreActionsAnchor)}
                      onClose={() => setMoreActionsAnchor(null)}
                    >
                      <MenuItem
                        onClick={() => {
                          setMoreActionsAnchor(null);
                          setShowDetachConfirm(true);
                        }}
                      >
                        <ListItemIcon>
                          <LinkOff fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Detach data</ListItemText>
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          setMoreActionsAnchor(null);
                          setShowDeleteConfirm(true);
                        }}
                      >
                        <ListItemIcon>
                          <Delete fontSize="small" color="error" />
                        </ListItemIcon>
                        <ListItemText>Delete document</ListItemText>
                      </MenuItem>
                    </Menu>
                  </>
                )}
              </>
            )}
          </>
        }
        // Fixed dialog height so the preview pane (which uses height: 100%)
        // has an anchor and renders correctly.
        sx={{
          "& .MuiDialog-paper": {
            height: "calc(100vh - 80px)",
            maxHeight: "1100px",
          },
        }}
        contentSx={{ pt: 0 }}
        // Pin the outer DialogContent so it doesn't scroll — that lets the form
        // column and preview column scroll independently inside it.
        DialogContentProps={{ sx: { overflowY: "hidden" } }}
        // The form column is `<Grid container>` (flex with wrap), so its bounded
        // height isn't reliable as a scroll target. Make it `position: relative`
        // so an absolutely-positioned inner Box can fill its bounds and scroll.
        formContainerSx={{
          position: "relative",
          overflowY: "visible",
          // Experimental: drop the form column's right padding so its scrollbar
          // sits flush against the preview, maximizing PDF width.
          pr: 0,
        }}
        previewUrl={previewUrl}
        fileExtension={document?.fileExtension ?? undefined}
        previewLoading={loading}
        showPreviewBoxIfFailed
        formColumnSize={{ xs: 5 }}
        previewColumnSize={{ xs: 7 }}
        contentColumnSpacing={0}
        previewDefaultScale="page-width"
        // The pane follows the field being edited: it opens on the page the
        // manifest is actually on, and outlines the box the value came off.
        previewHighlight={following.highlight}
        onPreviewPageCount={notePreviewPageCount}
        // The "Preview" label + full-screen control live in the title bar
        // (alongside the doc-info icons), so the pane fills from the top.
        hidePreviewHeader
        hideCancelButton={false}
        cancelButtonProps={{ text: "Close" }}
        additionalPrimaryActionElements={[
          ...(currentIndex > 0
            ? [
                <Button
                  key="back"
                  variant="outlined"
                  disabled={isBusy}
                  onClick={handlePrevious}
                  startIcon={<ChevronLeft />}
                >
                  Back
                </Button>,
              ]
            : []),
          ...(hasNext
            ? [
                <Button
                  key="skip"
                  variant="text"
                  disabled={isBusy}
                  onClick={() => setShowSkipConfirm(true)}
                  sx={{ color: "text.secondary" }}
                >
                  Skip
                </Button>,
              ]
            : []),
          // Partial save — only while the doc needs review (once reviewed the
          // primary "Save" already covers plain edits). Enabled only when there
          // are edits to persist; keeps the doc in Needs Review.
          ...(needsReview
            ? [
                <Button
                  key="partial-save"
                  variant="outlined"
                  disabled={isBusy || !manifest.manifestIsDirty || hasBlockingIssues}
                  onClick={handlePartialSave}
                >
                  Save
                </Button>,
              ]
            : []),
        ]}
        primaryActionButton={
          <Button
            variant="contained"
            disabled={isBusy || hasBlockingIssues || manifest.vendorMissing}
            onClick={handleSaveAndNext}
            startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : undefined}
            endIcon={hasNext && !isSaving ? <ChevronRight /> : undefined}
          >
            {primaryActionLabel}
          </Button>
        }
      >
        {/* Absolute-positioned inner Box is the scroll surface. Parent form
          column is `position: relative` so this Box fills its exact bounds
          regardless of how the Grid v2 container computes its height. */}
        <Box
          ref={scrollContainerRef}
          sx={{ position: "absolute", inset: 0, overflowY: "auto", pr: 2 }}
        >
          <Stack spacing={2} sx={{ pt: 1, minWidth: 0 }}>
            {manifest.manifestDataLoaded && manifest.manifestNeedsReview && (
              <Stack spacing={1}>
                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                  <ManifestFillProgress control={manifest.manifestControl} />
                  <Box flex={1} />
                  <AttentionListButton
                    attention={attention}
                    sections={MANIFEST_ATTENTION_SECTIONS}
                    errorKinds={MANIFEST_ATTENTION_ERROR_KINDS}
                  />
                </Stack>
                <ManifestKeyboardHints hasOffers={sourceOffers.manifestOffers.size > 0} />
              </Stack>
            )}
            {loading || manifest.manifestLoading ? (
              <ManifestReviewSkeleton />
            ) : !manifest.manifestDataLoaded ? (
              <NoManifestDataState
                extractionStatus={document?.extractionStatus ?? null}
                extractionError={document?.extractionError ?? null}
              />
            ) : (
              <Box
                ref={targetsRef}
                // The badge rule goes here rather than on each field: these inputs
                // are built through shared controls with no per-field wrapper to
                // reach, so it scopes itself with MUI's own field root.
                sx={{ minWidth: 0, ...REVEAL_SHORTCUTS_PER_FIELD }}
                onFocusCapture={(event) => {
                  following.onFocusCapture(event);
                  offers.focusProps.onFocusCapture(event);
                  // Travel counts from wherever the reviewer actually is, so a
                  // field they clicked into sets the position too.
                  const path = fieldPathOf(event.target);
                  if (path) markPosition(path);
                }}
                onBlurCapture={(event) => {
                  offers.focusProps.onBlurCapture(event);
                  keepOnTab.onBlurCapture(event);
                }}
                onKeyDownCapture={(event) => {
                  keepOnTab.onKeyDownCapture(event);
                  offers.focusProps.onKeyDownCapture(event);
                }}
              >
                <FieldAccessoryProvider render={renderFieldOffers}>
                  <ManifestDetailSection
                    manifest={manifest}
                    fieldDiffs={reviewFieldDiffIndex}
                    availableSources={reviewAvailableSources}
                    rowsWithOffers={openRows}
                  />
                </FieldAccessoryProvider>
              </Box>
            )}
          </Stack>
        </Box>
      </DocumentDialogContainer>

      {previewUrl && (
        <FullScreenDocumentPreview
          open={fullScreenOpen}
          onClose={() => setFullScreenOpen(false)}
          fileUrl={previewUrl}
          fileExtension={document?.fileExtension ?? undefined}
        />
      )}

      {showDetachConfirm && (
        <ConfirmDialog
          open
          onClose={() => setShowDetachConfirm(false)}
          onConfirm={async () => {
            try {
              await manifest.handleDetached();
              alerts.success("Manifest data detached. The source document has been kept.");
              setShowDetachConfirm(false);
              advanceOrClose();
            } catch (err) {
              alerts.error(
                "Failed to detach manifest data.",
                err instanceof Error ? err : undefined
              );
              setShowDetachConfirm(false);
            }
          }}
          header="Detach manifest data?"
          info="This will delete the parsed manifest data (waste manifest, waste lines, and transporters) but keep the source document."
          confirmText="Detach"
          loading={manifest.deletingManifest}
        />
      )}

      {showDeleteConfirm && (
        <ConfirmDialog
          open
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={async () => {
            try {
              await manifest.handleDeleteDocument();
              setShowDeleteConfirm(false);
              advanceOrClose();
            } catch {
              // handleDeleteDocument has already surfaced a specific alert
              // (partial-failure or detach-failure). Just dismiss the confirm.
              setShowDeleteConfirm(false);
            }
          }}
          header="Delete document?"
          info="This will permanently delete the source document and all associated manifest data (waste manifest, waste lines, and transporters). This cannot be undone."
          confirmText="Delete"
          loading={manifest.deletingDocument || manifest.deletingManifest}
        />
      )}

      {showDiscardConfirm && (
        <ConfirmDialog
          open
          onClose={() => setShowDiscardConfirm(false)}
          onConfirm={() => {
            setShowDiscardConfirm(false);
            onClose();
          }}
          header="Discard unsaved changes?"
          info="You have unsaved changes to this manifest. Closing now will lose them."
          confirmText="Discard"
        />
      )}

      {showSkipConfirm && (
        <ConfirmDialog
          open
          onClose={() => setShowSkipConfirm(false)}
          onConfirm={handleSkipConfirm}
          header="Skip without reviewing?"
          info="You can come back to this manifest at any time. It will stay marked as Needs Review until you mark it reviewed."
          confirmText="Skip"
        />
      )}
    </>
  );
}

function InfoRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <Stack direction="row" spacing={2} alignItems="flex-start">
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120, flexShrink: 0 }}>
        {label}
      </Typography>
      {typeof value === "string" ? (
        <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
          {value}
        </Typography>
      ) : (
        value
      )}
    </Stack>
  );
}

/**
 * Empty state for the form pane when there's a source document but no parsed
 * manifest data — extraction is still in flight, never ran, or failed. Without
 * this the dialog would sit in the skeleton state forever for these docs.
 * Copy adapts to the document's extractionStatus.
 */
function NoManifestDataState({
  extractionStatus,
  extractionError,
}: {
  extractionStatus: ExtractionStatus | null;
  extractionError: string | null;
}) {
  const isFailed = extractionStatus === ExtractionStatus.Failed;
  const isPending =
    extractionStatus === ExtractionStatus.Pending ||
    extractionStatus === ExtractionStatus.NotAttempted ||
    extractionStatus === null;
  const Icon = isFailed ? ErrorOutline : HourglassEmpty;
  const heading = isFailed
    ? "Couldn't read this manifest"
    : isPending
      ? "Still reading this manifest"
      : "No manifest data yet";
  const body = isFailed
    ? extractionError ||
      "We couldn't read the data off this document. Try processing it again from the manifest's Documents section, or review the source PDF on the right."
    : isPending
      ? "We're still reading the fields off this document. Refresh in a minute, or review the source PDF on the right while you wait."
      : "No manifest data is linked to this document yet. Review the source PDF on the right.";
  return (
    <Stack spacing={1.5} alignItems="center" sx={{ p: 4, textAlign: "center" }}>
      <Icon color={isFailed ? "error" : "action"} sx={{ fontSize: 40 }} />
      <Typography variant="subtitle1">{heading}</Typography>
      <Typography variant="body2" color="text.secondary">
        {body}
      </Typography>
    </Stack>
  );
}

/**
 * Loading-state shape that mirrors the eventual ManifestDetailSection layout —
 * section headers + grouped field skeletons. Cleaner than showing the real
 * accordion with empty/placeholder values while data is in flight.
 */
function ManifestReviewSkeleton() {
  return (
    <Stack spacing={3} sx={{ p: 1 }}>
      {/* Help text */}
      <Skeleton variant="text" width="80%" height={20} />

      {/* Generator ID + Tracking Number */}
      <Skeleton variant="rounded" height={48} />
      <Skeleton variant="rounded" height={48} />

      {/* Generator: name + mailing + site address */}
      <Skeleton variant="text" width={140} height={28} />
      <Skeleton variant="rounded" height={48} />
      <Skeleton variant="rounded" height={48} />
      <Stack direction="row" spacing={2}>
        <Skeleton variant="rounded" height={48} sx={{ flex: 2 }} />
        <Skeleton variant="rounded" height={48} sx={{ flex: 1 }} />
        <Skeleton variant="rounded" height={48} sx={{ flex: 1.5 }} />
      </Stack>
      <Skeleton variant="rounded" height={48} />
      <Stack direction="row" spacing={2}>
        <Skeleton variant="rounded" height={48} sx={{ flex: 2 }} />
        <Skeleton variant="rounded" height={48} sx={{ flex: 1 }} />
        <Skeleton variant="rounded" height={48} sx={{ flex: 1.5 }} />
      </Stack>

      {/* Transporters (collapsed accordion) */}
      <Skeleton variant="text" width={200} height={28} />
      <Skeleton variant="rounded" height={56} />

      {/* Designated facility */}
      <Skeleton variant="text" width={200} height={28} />
      <Skeleton variant="rounded" height={48} />
      <Skeleton variant="rounded" height={48} />
      <Skeleton variant="rounded" height={48} />
      <Stack direction="row" spacing={2}>
        <Skeleton variant="rounded" height={48} sx={{ flex: 2 }} />
        <Skeleton variant="rounded" height={48} sx={{ flex: 1 }} />
        <Skeleton variant="rounded" height={48} sx={{ flex: 1.5 }} />
      </Stack>

      {/* Line items (collapsed accordion) */}
      <Skeleton variant="text" width={180} height={28} />
      <Skeleton variant="rounded" height={56} />

      {/* Shipped + received dates */}
      <Stack direction="row" spacing={2}>
        <Skeleton variant="rounded" height={48} sx={{ flex: 1 }} />
        <Skeleton variant="rounded" height={48} sx={{ flex: 1 }} />
      </Stack>
    </Stack>
  );
}
